import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { checkAuth, useAuthInfo } from '../../../lib/jwt';
import { PollAnswerDtoType } from '../../../types/dto/PollAnswerDtoType';
import { PollDtoType } from '../../../types/dto/PollDtoType';
import { getVoteCount } from '../../../helpers/getVoteCount';
import { ASSETS_BASE_URL } from '../../../lib/constants';

type TextPollContentProps = {
  className?: string;
  poll: PollDtoType;
  onVote: (id: number) => any;
  pollEnded?: boolean;
  hasSpoiler?: boolean;
};

type VoteInputProps = {
  answer: PollAnswerDtoType;
  index: number;
  answersCount: number;
  onVote: (id: number) => any;
  hasSpoiler?: boolean;
};

type VoteDisplayProps = {
  answer: PollAnswerDtoType;
  index: number;
  answersCount: number;
  votesTotal: number;
  userVote: number;
  pollEnded?: boolean;
  hasSpoiler?: boolean;
};

function VoteInput({ answer, index, answersCount, onVote, hasSpoiler }: VoteInputProps) {
  const router = useRouter();
  const [spoilerShow, setSpoilerShow] = useState<boolean>(false);

  return (
    <button
      className={`group relative rounded-lg cursor-pointer overflow-hidden text-left ${
        index < answersCount - 1 ? 'mb-2' : ''
      } bg-navy-light active:bg-red-light outline-none focus:outline-none`}
      onClick={() => {
        if ((hasSpoiler && spoilerShow) || !hasSpoiler)
          checkAuth(() => onVote(answer.id), router);
      }}
    >
      {hasSpoiler && !spoilerShow ? (
        <div
          onClick={() => {
            setSpoilerShow(true);
          }}
          className="bg-gray-spoilerBg flex justify-center items-center flex-col text-gray-spoilerText text-center"
        >
          <div className="hover-pointer flex gap-x-2 px-4 py-2 items-center">
            <p>This poll contains spoilers</p>
            <img src={`${ASSETS_BASE_URL}system/cw-assets/spoiler-eye.svg`}></img>
            <p className="text-white font-bold">View Poll</p>
          </div>
        </div>
      ) : (
        <div className="px-4 py-2">{answer.text}</div>
      )}
      {!hasSpoiler ? (
        <div
          className={`absolute justify-end px-4 py-2 left-0 top-0 w-full h-full font-bold hidden group-hover:flex bg-gradient-to-r from-transparent via-transparent to-red-light`}
        >
          Vote
        </div>
      ) : (
        hasSpoiler &&
        spoilerShow && (
          <div
            className={`absolute justify-end px-4 py-2 left-0 top-0 w-full h-full font-bold hidden group-hover:flex bg-gradient-to-r from-transparent via-transparent to-red-light`}
          >
            Vote
          </div>
        )
      )}
    </button>
  );
}

function VoteDisplay({
  answer,
  index,
  answersCount,
  votesTotal,
  userVote,
  pollEnded,
  hasSpoiler
}: VoteDisplayProps) {
  const percentValue = getVoteCount(answer.votesCount, votesTotal);
  const [authInfo] = useAuthInfo(false);
  const [spoilerShow, setSpoilerShow] = useState<boolean>(false);

  const voted = authInfo !== undefined && answer.id === userVote;

  return (
    <div
      className={`${hasSpoiler &&
        pollEnded &&
        !spoilerShow &&
        'relative flex justify-between rounded-lg overflow-hidden'} ${
        index < answersCount - 1 ? 'mb-2' : ''
      } bg-purple-dark`}
    >
      {hasSpoiler && pollEnded && !spoilerShow ? (
        <div
          onClick={() => {
            setSpoilerShow(true);
          }}
          className="bg-gray-spoilerBg w-full flex justify-center items-center flex-col text-gray-spoilerText text-center"
        >
          <div className="hover-pointer flex gap-x-2 px-4 py-2 items-center">
            <p>This poll contains spoilers</p>
            <img src={`${ASSETS_BASE_URL}system/cw-assets/spoiler-eye.svg`}></img>
            <p className="text-white font-bold">View Poll</p>
          </div>
        </div>
      ) : (
        <div
          className={`relative flex justify-between rounded-lg overflow-hidden ${
            index < answersCount - 1 ? 'mb-2' : ''
          } bg-purple-dark`}
        >
          <div
            className={`absolute left-0 top-0 h-full ${
              voted ? 'bg-red-light' : 'bg-navy-light'
            }`}
            style={{ width: `${percentValue}%` }}
          ></div>
          <div className="relative px-4 py-2">{answer.text}</div>
          <div className="relative px-4 py-2">{percentValue}%</div>
        </div>
      )}
    </div>
  );
}

export function TextPollContent({
  poll,
  className,
  onVote,
  pollEnded,
  hasSpoiler
}: TextPollContentProps) {
  const votesTotal = poll.pollAnswers.reduce(
    (prev, curr) => prev + (curr.votesCount || 0),
    0
  );
  const [authInfo] = useAuthInfo(false);

  const voted = authInfo !== undefined && !!poll.userVote;

  return (
    <div className={`flex flex-col ${className || ''}`}>
      {pollEnded
        ? poll.pollAnswers?.map((answer, i) => (
            <VoteDisplay
              key={answer.id}
              answer={answer}
              index={i}
              answersCount={poll.pollAnswers.length}
              votesTotal={votesTotal}
              userVote={poll.userVote}
              hasSpoiler={hasSpoiler}
              pollEnded={pollEnded}
            />
          ))
        : poll.pollAnswers?.map((answer, i) =>
            !voted ? (
              <VoteInput
                key={answer.id}
                answer={answer}
                index={i}
                answersCount={poll.pollAnswers.length}
                onVote={onVote}
                hasSpoiler={hasSpoiler}
              />
            ) : (
              <VoteDisplay
                key={answer.id}
                answer={answer}
                index={i}
                answersCount={poll.pollAnswers.length}
                votesTotal={votesTotal}
                userVote={poll.userVote}
              />
            )
          )}
    </div>
  );
}
